import { LOGIN_ACTION, LoginAction } from 'actions/authorization/loginAction'
import { LOGOUT_ACTION, LogoutAction } from 'actions/authorization/logoutAction'
import {
  FETCH_REQUEST_INCOGNITO_STATUS,
  fetchRequestIncognitoStatusAction,
} from 'actions/profile/fetchRequestIncognitoStatusAction'
import {
  fetchProfileFieldsCompletenessAction,
  FETCH_PROFILE_FIELDS_COMPLETENESS,
  clearProfileFieldsCompletenessAction,
  CLEAR_PROFILE_FIELDS_COMPLETENESS,
} from 'actions/profile/profileDatingAction'
import {
  RESET_REQUEST_INCOGNITO_STATUS,
  resetRequestIncognitoStatusAction,
} from 'actions/profile/resetRequestIncognitoStatusAction'
import {
  FETCH_TEAMO_TOKEN,
  fetchTeamoTokenAction,
} from 'actions/profile/teamoCurrentProfileAction'
import {
  TOGGLE_UPDATE_PROFILE,
  toggleProfileUpdateAction,
} from 'actions/profile/toggleProfileUpdateAction'
import {
  FETCH_STATUS_VERIFICATION_LIVENESS,
  fetchStatusVerificationLivenessAction,
} from 'actions/settings/photoVerificationLivenessAction'
import {
  LivenessComet,
  SOCKET_RECEIVE_DATA,
  SocketData,
  SocketReceiveDataAction,
} from 'actions/socket/socketReceiveDataAction'
import { definitions } from 'api/generated/incognito'
import { createLivenessSessionResultChannel } from 'functions/comet'
import { defaultPromiseReducer } from 'reducers/defaultPromiseReducer'
import { filterCometMessages } from 'reducers/functions'
import { createTeamoUrl } from 'reducers/profile/function/teamo'

export interface ProfileState {
  isForceUpdateProfile: boolean
  incognitoRequestStatus: null | definitions['UserIncognitoRequestStatus']
  teamo: {
    url: string | null
  }
  profileFieldsCompleteness: {
    isLoading: boolean
    isLoaded: boolean
    incomplete: number
    total: number
  }
}

export const initialState: ProfileState = {
  isForceUpdateProfile: false,
  incognitoRequestStatus: null,
  teamo: {
    url: null,
  },

  profileFieldsCompleteness: {
    isLoading: false,
    isLoaded: false,
    incomplete: 0,
    total: 0,
  },
}

export const profileReducer = (
  state = initialState,
  action:
    | ReturnType<typeof toggleProfileUpdateAction>
    | ReturnType<typeof fetchTeamoTokenAction>
    | ReturnType<typeof fetchProfileFieldsCompletenessAction>
    | ReturnType<typeof clearProfileFieldsCompletenessAction>
    | ReturnType<typeof fetchRequestIncognitoStatusAction>
    | ReturnType<typeof resetRequestIncognitoStatusAction>
    | ReturnType<typeof fetchStatusVerificationLivenessAction>
    | SocketReceiveDataAction
    | LoginAction
    | LogoutAction
) => {
  switch (action.type) {
    case TOGGLE_UPDATE_PROFILE:
      return {
        ...state,
        isForceUpdateProfile: action.value,
      }

    case FETCH_STATUS_VERIFICATION_LIVENESS:
      return defaultPromiseReducer(state, action, undefined, (result) => {
        if (result?.status === 'verified') {
          return {
            isForceUpdateProfile: true,
          }
        }

        return state
      })

    case FETCH_TEAMO_TOKEN:
      return defaultPromiseReducer(state, action, undefined, (result) => {
        return {
          teamo: {
            url: createTeamoUrl(result.aid, result.token),
          },
        }
      })

    case SOCKET_RECEIVE_DATA:
      return defaultPromiseReducer(state, action, undefined, () => {
        const [liveness] = filterCometMessages<LivenessComet>(
          action.result as SocketData<LivenessComet>[],
          createLivenessSessionResultChannel(action.userId)
        )

        if (liveness?.content?.sessionStatus === 'verified') {
          return {
            isForceUpdateProfile: true,
          }
        }

        return state
      })

    case FETCH_PROFILE_FIELDS_COMPLETENESS:
      return defaultPromiseReducer(
        state,
        action,
        () => ({
          profileFieldsCompleteness: {
            ...state.profileFieldsCompleteness,
            isLoading: true,
          },
        }),
        (result) => {
          const { incomplete, total } = result
          return {
            profileFieldsCompleteness: {
              isLoaded: true,
              isLoading: false,
              incomplete,
              total,
            },
          }
        },
        () => ({
          profileFieldsCompleteness: {
            ...state.profileFieldsCompleteness,
            isLoaded: true,
            isLoading: false,
          },
        })
      )

    case CLEAR_PROFILE_FIELDS_COMPLETENESS:
      return {
        ...state,
        profileFieldsCompleteness: {
          ...initialState.profileFieldsCompleteness,
        },
      }

    case FETCH_REQUEST_INCOGNITO_STATUS:
      return defaultPromiseReducer(state, action, undefined, (result) => ({
        incognitoRequestStatus: result,
      }))

    case RESET_REQUEST_INCOGNITO_STATUS:
      return {
        ...state,
        incognitoRequestStatus: null,
      }

    case LOGIN_ACTION:
    case LOGOUT_ACTION:
      return {
        ...initialState,
      }

    default:
      return state
  }
}
